/**
 * __FLEET_TRACKING__ (2026-10-03) the Fleet Tracking tab.
 *
 * Henry: "Add a tab for me to live view all GPS tracked activity. Give me powerful tools to
 * explore everything the trucks have been doing, give me a timeline view so I can slide the
 * daily time bar to any time of day and see the entire fleet at any moment ... strong history
 * view ... hover over any truck and get a glimpse of the loads assigned."
 *
 *  - Live: every driver's newest position (heading arrow when moving), state, speed, age;
 *    polls gps_locations every 15 s (the table is not in the realtime publication).
 *  - Time bar: any day, scrub 24 h, play 1x/10x/60x/300x; positions interpolate between pings;
 *    trails of the last N minutes; one activity lane per driver (driving / stopped / no GPS).
 *  - History: per driver/day speed-coloured trail, stops, gaps, geofence events, stop visits,
 *    sign-on/off, day summary, jobs-vs-GPS, CSV / GPX export.
 *  - Tools: search + filters, fit fleet, follow, distance measure, coverage heat, speed alerts.
 *  - Off shift: the driver is grey at his last position from BEFORE the sign-off, never later
 *    (same rule as the Fleet Map, utils/offShift.js).
 *
 * Pure maths in utils/fleetTrack.js (sim/fleettrack.test.mjs); reads in services/fleetTrackingService.js.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import FleetTrackingMap from './FleetTrackingMap';
import FleetHistoryPanel from './FleetHistoryPanel';
import { ensureFleetMaps, onFleetMapsAuthFailure } from '../services/fleetMapsLoader';
import {
  fetchFleetRoster, fetchFleetTracks, fetchNewPings, fetchLastPings,
  fetchDayLoads, fetchDayGeofenceEvents, fetchDayShifts,
} from '../services/fleetTrackingService';
import { fetchShiftStates } from '../services/shiftStatusService';
import { isOffShift } from '../utils/offShift';
import {
  tsMs, dayBounds, localYmd, normalizePings, mergeTrack, positionAt, trailWindow, indexAtOrBefore,
  detectGaps, detectStops, daySummary, speedingEvents, speedSegments, liveState, STATE_META,
  activityBlocks, compareStopsToGps, coverageCells, pathKm, trackToCsv, trackToGpx,
  fmtDur, fmtClock, fmtAge, SPEED_BANDS,
} from '../utils/fleetTrack';

const POLL_MS = 15000;
const SLOW_POLL_MS = 60000;
const GAP_MIN = 5;
const DRIVER_COLORS = ['#58a6ff', '#3fb950', '#f0883e', '#a371f7', '#ff7b72', '#39c5cf', '#d29922', '#db61a2', '#7ee787', '#79c0ff', '#ffa657', '#d2a8ff'];
const SETTINGS_KEY = 'hmgc_fleet_tracking';
const DEFAULTS = { speedLimit: 105, staleMin: 10, dwellMin: 5, trailMin: 30, showStops: true, showEvents: true, heat: false, laneOpen: true };

const loadSettings = () => {
  try { return { ...DEFAULTS, ...(JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}') || {}) }; } catch (e) { return { ...DEFAULTS }; }
};
const saveSettings = (s) => { try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(s)); } catch (e) { /* private window */ } };

const download = (name, text, type) => {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const stopLabel = (s) => s.place_name || (s.address || '').split(',')[0] || s.city || 'Stop';
const stopArr = (s) => (tsMs(s.actual_arrival_time) != null ? tsMs(s.actual_arrival_time) : tsMs(s.actual_arrival));
const stopDep = (s) => (tsMs(s.actual_departure_time) != null ? tsMs(s.actual_departure_time) : tsMs(s.actual_departure));
const stopPlan = (s) => (tsMs(s.planned_arrival_at) != null ? tsMs(s.planned_arrival_at) : (tsMs(s.estimated_arrival_time) != null ? tsMs(s.estimated_arrival_time) : tsMs(s.estimated_arrival)));

/** The driver's next stop: first unfinished stop of an unfinished load, in planned order. */
const nextStopOf = (loads) => {
  const cands = [];
  (loads || []).filter((l) => !l.finished).forEach((l) => (l.load_stops || []).forEach((s) => {
    if (s.completed || stopDep(s) != null) return;
    cands.push({ s, l, plan: stopPlan(s), at: stopArr(s) != null });
  }));
  cands.sort((a, b) => (b.at - a.at) || ((a.plan == null ? 9e15 : a.plan) - (b.plan == null ? 9e15 : b.plan)));
  return cands[0] || null;
};

const chip = (color, text, extra) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0.1rem 0.45rem', borderRadius: 10, border: `1px solid ${color}`, color, background: color + '1f', fontSize: '0.7rem', fontWeight: 600, whiteSpace: 'nowrap', ...(extra || {}) }}>{text}</span>
);

const btn = (active) => ({
  padding: '0.35rem 0.65rem', background: active ? 'var(--btn-primary-bg)' : 'var(--btn-secondary-bg)', color: 'var(--text-primary)',
  border: active ? '1px solid var(--btn-primary-bg)' : '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontSize: '0.78rem', fontWeight: active ? 600 : 400, whiteSpace: 'nowrap',
});
const inputStyle = { background: 'var(--surface-input)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '0.3rem 0.45rem', fontSize: '0.8rem' };

function FleetTrackingView() {
  const [settings, setSettings] = useState(loadSettings);
  const [roster, setRoster] = useState({ drivers: [], trucks: [] });
  const [rosterError, setRosterError] = useState(null);
  const [ymd, setYmd] = useState(() => localYmd());
  const [now, setNow] = useState(() => Date.now());
  const [live, setLive] = useState(true);
  const [cursor, setCursor] = useState(() => Date.now());
  const [playing, setPlaying] = useState(false);
  const [playSpeed, setPlaySpeed] = useState(60);
  const [tracks, setTracks] = useState({});
  const [trackMeta, setTrackMeta] = useState({});
  const [lastPings, setLastPings] = useState({});
  const [loads, setLoads] = useState([]);
  const [gfEvents, setGfEvents] = useState([]);
  const [dayShifts, setDayShifts] = useState([]);
  const [shiftStates, setShiftStates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [lastPoll, setLastPoll] = useState(null);
  const [mapsState, setMapsState] = useState({ status: 'loading', message: '' });
  const [search, setSearch] = useState('');
  const [hidden, setHidden] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [hover, setHover] = useState(null);
  const [follow, setFollow] = useState(false);
  const [tab, setTab] = useState('fleet');
  const [measureOn, setMeasureOn] = useState(false);
  const [measurePts, setMeasurePts] = useState([]);
  const [fitReq, setFitReq] = useState({ n: 0, mode: 'fleet' });
  const [panTarget, setPanTarget] = useState(null);
  const lastCreatedRef = useRef(null);
  const reqRef = useRef(0);
  const autoFitDone = useRef(false);
  const wrapRef = useRef(null);
  const mapBoxRef = useRef(null);

  const bounds = useMemo(() => dayBounds(ymd), [ymd]);
  const isToday = ymd === localYmd(now);
  const setSetting = useCallback((k, v) => setSettings((s) => { const n = { ...s, [k]: v }; saveSettings(n); return n; }), []);

  // Google Maps through the board's shared loader.
  useEffect(() => {
    let alive = true;
    const AUTH_MSG = 'Google refused the board\'s Maps key, so the map cannot be drawn. Check the key and that "Maps JavaScript API" is enabled for it. The fleet list, time bar and history below still work.';
    const unsubAuth = onFleetMapsAuthFailure(() => { if (alive) setMapsState({ status: 'error', message: AUTH_MSG }); });
    ensureFleetMaps()
      .then(() => { if (alive) setMapsState({ status: 'ready', message: '' }); })
      .catch((e) => {
        if (!alive) return;
        setMapsState({
          status: 'error',
          message: e && e.code === 'AUTH_FAILED' ? AUTH_MSG : e && e.code === 'NO_KEY'
            ? 'No Google Maps key is set for the board, so the map cannot be drawn. The owner sets it in Settings → Google Maps (Google Maps API Key). The fleet list, time bar and history below still work.'
            : `Google Maps did not load (${(e && e.message) || 'unknown error'}). Check the key and that "Maps JavaScript API" is enabled for it.`,
        });
      });
    return () => { alive = false; unsubAuth(); };
  }, []);

  // Roster once.
  useEffect(() => {
    fetchFleetRoster().then(setRoster).catch((e) => setRosterError(e.message || String(e)));
  }, []);

  // Wall clock.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(t);
  }, []);

  // In live mode the cursor IS now.
  useEffect(() => {
    if (live && isToday) setCursor(now);
  }, [live, isToday, now]);

  const driverIds = useMemo(() => roster.drivers.map((d) => String(d.id)), [roster.drivers]);
  const driverById = useMemo(() => {
    const m = {};
    roster.drivers.forEach((d) => { m[String(d.id)] = d; });
    return m;
  }, [roster.drivers]);
  const truckById = useMemo(() => {
    const m = {};
    roster.trucks.forEach((t) => { m[String(t.id)] = t; });
    return m;
  }, [roster.trucks]);

  // Day data: tracks, loads, geofence events, shifts (and last-ever pings / live shift state for today).
  const loadDay = useCallback(async () => {
    if (!bounds || !driverIds.length) return;
    const req = ++reqRef.current;
    const today = ymd === localYmd();
    setLoading(true);
    setProgress({ done: 0, total: driverIds.length });
    const startedAt = Date.now();
    try {
      const uids = roster.drivers.map((d) => d.user_id).filter(Boolean).map(String);
      const [{ tracks: tr, meta }, dayLoads, events, shifts, last, st] = await Promise.all([
        fetchFleetTracks(driverIds, bounds.startMs, bounds.endMs, (done, total) => { if (reqRef.current === req) setProgress({ done, total }); }),
        fetchDayLoads(bounds.startMs, bounds.endMs, { includeActive: today, ymd }),
        fetchDayGeofenceEvents(driverIds, bounds.startMs, bounds.endMs),
        fetchDayShifts(uids, bounds.startMs, bounds.endMs),
        today ? fetchLastPings(driverIds) : Promise.resolve({}),
        today ? fetchShiftStates() : Promise.resolve(null),
      ]);
      if (reqRef.current !== req) return;
      setTracks(tr);
      setTrackMeta(meta);
      setLoads(dayLoads);
      setGfEvents(events);
      setDayShifts(shifts);
      setLastPings(last);
      setShiftStates(st);
      lastCreatedRef.current = new Date(startedAt - 120000).toISOString().replace('Z', '');
      setLastPoll(new Date());
    } catch (e) {
      console.error('Fleet day load error', e);
    } finally {
      if (reqRef.current === req) { setLoading(false); setProgress(null); }
    }
  }, [bounds, driverIds, ymd, roster.drivers]);

  useEffect(() => {
    autoFitDone.current = false;
    setTracks({});
    setLoads([]);
    setGfEvents([]);
    setDayShifts([]);
    if (ymd !== localYmd()) {
      setLive(false);
      setCursor((c) => (bounds && (c < bounds.startMs || c >= bounds.endMs) ? bounds.startMs + 12 * 3600000 : c));
    }
    loadDay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ymd, driverIds.length]);

  // Live polling (today only): new pings every 15 s, loads / events / shifts every 60 s.
  useEffect(() => {
    if (!isToday || !driverIds.length) return undefined;
    let busy = false;
    const poll = async () => {
      if (busy) return;
      busy = true;
      try {
        const rows = await fetchNewPings(driverIds, lastCreatedRef.current);
        if (rows.length) {
          const by = {};
          rows.forEach((r) => { const id = String(r.driver_id); (by[id] = by[id] || []).push(r); });
          const maxCreated = rows.reduce((m, r) => (r.created_at && r.created_at > m ? r.created_at : m), lastCreatedRef.current || '');
          lastCreatedRef.current = maxCreated;
          const b = dayBounds(localYmd());
          setTracks((prev) => {
            const next = { ...prev };
            Object.keys(by).forEach((id) => {
              const add = normalizePings(by[id]).filter((p) => !b || (p.t >= b.startMs && p.t < b.endMs));
              if (add.length) next[id] = mergeTrack(prev[id], add);
            });
            return next;
          });
        }
        setLastPoll(new Date());
      } catch (e) {
        console.warn('Fleet poll error', e.message || e);
      } finally { busy = false; }
    };
    const slow = async () => {
      const b = dayBounds(localYmd());
      if (!b) return;
      try {
        const [dl, ev, st] = await Promise.all([
          fetchDayLoads(b.startMs, b.endMs, { includeActive: true, ymd: localYmd() }),
          fetchDayGeofenceEvents(driverIds, b.startMs, b.endMs),
          fetchShiftStates(),
        ]);
        setLoads(dl); setGfEvents(ev); setShiftStates(st);
      } catch (e) { console.warn('Fleet slow poll error', e.message || e); }
    };
    const t1 = setInterval(poll, POLL_MS);
    const t2 = setInterval(slow, SLOW_POLL_MS);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, [isToday, driverIds]);

  // Playback.
  useEffect(() => {
    if (!playing || !bounds) return undefined;
    const t = setInterval(() => {
      setCursor((c) => {
        const n = c + 250 * playSpeed;
        const end = Math.min(bounds.endMs - 1, Date.now());
        if (n >= end) {
          setPlaying(false);
          if (ymd === localYmd()) setLive(true);
          return end;
        }
        return n;
      });
    }, 250);
    return () => clearInterval(t);
  }, [playing, playSpeed, bounds, ymd]);

  // ---- per-driver derived data ----
  const loadsByDriver = useMemo(() => {
    const m = {};
    loads.forEach((l) => { if (l.driver_id) (m[String(l.driver_id)] = m[String(l.driver_id)] || []).push(l); });
    return m;
  }, [loads]);

  const shiftsByDriver = useMemo(() => {
    const byUid = {};
    dayShifts.forEach((s) => { (byUid[String(s.user_id)] = byUid[String(s.user_id)] || []).push(s); });
    const m = {};
    roster.drivers.forEach((d) => { if (d.user_id && byUid[String(d.user_id)]) m[String(d.id)] = byUid[String(d.user_id)]; });
    return m;
  }, [dayShifts, roster.drivers]);

  const eventsByDriver = useMemo(() => {
    const m = {};
    gfEvents.forEach((e) => { if (e.driver_id) (m[String(e.driver_id)] = m[String(e.driver_id)] || []).push(e); });
    return m;
  }, [gfEvents]);

  const analysis = useMemo(() => {
    const out = {};
    Object.keys(tracks).forEach((id) => {
      const tr = tracks[id];
      const stops = detectStops(tr, { minDwellMin: settings.dwellMin, radiusM: 120 });
      out[id] = { stops, blocks: activityBlocks(tr, { stops, gapMin: GAP_MIN }) };
    });
    return out;
  }, [tracks, settings.dwellMin]);

  const truckOf = useCallback((id) => {
    const d = driverById[id];
    const ls = loadsByDriver[id] || [];
    const active = ls.find((l) => !l.finished && l.truck_id);
    const tid = (d && d.current_truck_id) || (trackMeta[id] && trackMeta[id].truckId) || (lastPings[id] && lastPings[id].truckId) || (active && active.truck_id);
    return tid ? truckById[String(tid)] || null : null;
  }, [driverById, loadsByDriver, trackMeta, lastPings, truckById]);

  const colorOf = useCallback((id) => DRIVER_COLORS[Math.max(0, driverIds.indexOf(id)) % DRIVER_COLORS.length], [driverIds]);

  /** Everyone's position + state at the cursor. */
  const fleet = useMemo(() => {
    const liveNow = live && isToday;
    const list = [];
    driverIds.forEach((id) => {
      const d = driverById[id];
      const tr = tracks[id] || [];
      const truck = truckOf(id);
      const name = (d && d.name) || 'Driver';
      const off = liveNow && shiftStates && isOffShift(shiftStates.shifts, id);
      let pos = null;
      let state = 'nodata';
      let lastT = null;
      if (liveNow) {
        let last = tr.length ? tr[tr.length - 1] : (lastPings[id] || null);
        if (off) {
          // off shift: last position from BEFORE the sign-off, nothing later (utils/offShift.js rule)
          const since = tsMs(shiftStates.shifts[id] && shiftStates.shifts[id].since);
          if (since != null && tr.length) {
            const i = indexAtOrBefore(tr, since);
            last = i >= 0 ? tr[i] : (lastPings[id] && lastPings[id].t <= since ? lastPings[id] : null);
          } else if (since != null && last && last.t > since) last = null;
          state = 'offshift';
          if (last) pos = { lat: last.lat, lng: last.lng, spd: null, hdg: null, t: last.t };
        } else if (last) {
          state = liveState(last, now, { staleMin: settings.staleMin, offlineMin: 30 });
          pos = { lat: last.lat, lng: last.lng, spd: last.spd, hdg: last.hdg, t: last.t };
        }
        lastT = last ? last.t : null;
      } else if (tr.length) {
        const p = positionAt(tr, cursor, { maxGapMs: GAP_MIN * 60000 });
        if (p) {
          pos = p;
          lastT = p.t;
          if (p.after && p.ageMs > 30 * 60000) state = 'offline';
          else if (p.after && p.ageMs > settings.staleMin * 60000) state = 'stale';
          else if (p.gap) state = 'stale';
          else state = p.spd != null && p.spd > 5 ? 'moving' : 'idle';
        }
      }
      const speeding = pos && pos.spd != null && pos.spd > settings.speedLimit && (state === 'moving');
      const dwellStop = (analysis[id] && pos && state === 'idle') ? analysis[id].stops.find((s) => cursor >= s.arriveMs && cursor <= s.departMs + 60000) : null;
      list.push({ id, name, truck, color: colorOf(id), pos, state, lastT, off, speeding, dwellStop, hasTrack: tr.length > 0, loads: loadsByDriver[id] || [] });
    });
    return list;
  }, [driverIds, driverById, tracks, truckOf, live, isToday, shiftStates, lastPings, now, cursor, settings.staleMin, settings.speedLimit, analysis, colorOf, loadsByDriver]);

  // Drivers worth listing: GPS today, a recent last ping, or a load today.
  const listed = useMemo(() => {
    const q = search.trim().toLowerCase();
    return fleet.filter((f) => {
      const recent = f.hasTrack || f.loads.length > 0 || (lastPings[f.id] && now - lastPings[f.id].t < 14 * 86400000);
      if (!recent) return false;
      if (!q) return true;
      const hay = [f.name, f.truck && f.truck.truck_number, f.truck && f.truck.license_plate, ...f.loads.map((l) => l.load_number)].join(' ').toLowerCase();
      return hay.includes(q);
    }).sort((a, b) => {
      const rank = { moving: 0, idle: 1, stale: 2, offshift: 3, offline: 4, nodata: 5 };
      return (rank[a.state] - rank[b.state]) || a.name.localeCompare(b.name);
    });
  }, [fleet, search, lastPings, now]);
  const hiddenCount = fleet.length - listed.length;
  const visible = useMemo(() => listed.filter((f) => !hidden[f.id]), [listed, hidden]);

  const counts = useMemo(() => {
    const c = { moving: 0, idle: 0, stale: 0, offline: 0, offshift: 0, nodata: 0 };
    listed.forEach((f) => { c[f.state] = (c[f.state] || 0) + 1; });
    return c;
  }, [listed]);
  const speeders = useMemo(() => (live && isToday ? visible.filter((f) => f.speeding) : []), [visible, live, isToday]);

  const selected = selectedId ? fleet.find((f) => f.id === selectedId) || null : null;

  // ---- map layers ----
  const markers = useMemo(() => visible.filter((f) => f.pos).map((f) => ({
    id: f.id,
    lat: f.pos.lat,
    lng: f.pos.lng,
    hdg: f.pos.hdg,
    arrow: f.state === 'moving' && f.pos.hdg != null,
    color: f.state === 'offshift' ? STATE_META.offshift.color : (f.state === 'moving' || f.state === 'idle' ? f.color : STATE_META[f.state].color),
    label: f.truck ? `${f.truck.truck_number}` : f.name.split(' ')[0],
    sub: f.state === 'moving' && f.pos.spd != null ? `${Math.round(f.pos.spd)} km/h` : (f.state === 'idle' ? (f.dwellStop ? fmtDur((cursor - f.dwellStop.arriveMs) / 60000) : 'stopped') : STATE_META[f.state].label.toLowerCase()),
    selected: f.id === selectedId,
    faded: f.state === 'offline' || f.state === 'offshift' || f.state === 'nodata',
    alert: !!f.speeding,
  })), [visible, selectedId, cursor]);

  const selTrack = useMemo(() => (selectedId && tracks[selectedId]) || [], [selectedId, tracks]);
  const selStops = useMemo(() => (selectedId && analysis[selectedId] ? analysis[selectedId].stops : []), [selectedId, analysis]);
  const selGaps = useMemo(() => detectGaps(selTrack, GAP_MIN), [selTrack]);
  const selSummary = useMemo(() => daySummary(selTrack, { stops: selStops, gapMin: GAP_MIN }), [selTrack, selStops]);
  const selSpeeding = useMemo(() => speedingEvents(selTrack, settings.speedLimit, { gapMin: GAP_MIN }), [selTrack, settings.speedLimit]);
  const selLoads = useMemo(() => (selectedId ? loadsByDriver[selectedId] || [] : []), [selectedId, loadsByDriver]);
  const selCompare = useMemo(() => compareStopsToGps(selLoads.flatMap((l) => l.load_stops || []), selTrack, { stops: selStops, radiusM: 300 }), [selLoads, selTrack, selStops]);
  const selSegments = useMemo(() => speedSegments(selTrack, { gapMin: GAP_MIN }), [selTrack]);

  const polylines = useMemo(() => {
    const out = [];
    // the selected driver's whole day, speed-coloured, faint beyond the cursor
    if (selectedId && selTrack.length) {
      selSegments.forEach((s) => out.push(s.gap
        ? { path: s.path, color: '#f85149', dashed: true, z: 2 }
        : { path: s.path, color: s.color, opacity: 0.85, weight: 4, z: 3 }));
    }
    // trails of the last N minutes for everyone visible
    if (settings.trailMin > 0) {
      visible.forEach((f) => {
        if (f.id === selectedId && selTrack.length) return;
        const tr = tracks[f.id];
        if (!tr || !tr.length || (f.state === 'offshift')) return;
        const pts = trailWindow(tr, live && isToday ? now : cursor, settings.trailMin, { maxGapMs: GAP_MIN * 60000 });
        if (pts.length >= 2) out.push({ path: pts.map((p) => ({ lat: p.lat, lng: p.lng })), color: f.color, opacity: 0.75, weight: 3, z: 2 });
      });
    }
    return out;
  }, [selectedId, selTrack, selSegments, settings.trailMin, visible, tracks, live, isToday, now, cursor]);

  // Clickable dots: the selected driver's stops / events / load stops (index -> jump target).
  const dotList = useMemo(() => {
    const out = [];
    if (!selectedId) return out;
    if (settings.showStops) {
      selStops.forEach((s, i) => out.push({ lat: s.lat, lng: s.lng, color: '#d29922', size: 8, label: i + 1, title: `Stop ${i + 1}: ${fmtClock(s.arriveMs)} → ${fmtClock(s.departMs)} (${fmtDur(s.durMin)})`, jump: s.arriveMs, z: 60 }));
      selSpeeding.forEach((e) => out.push({ lat: e.lat, lng: e.lng, color: '#f85149', size: 4, title: `${e.maxKmh} km/h at ${fmtClock(e.startMs)}`, jump: e.startMs, z: 55 }));
    }
    if (settings.showEvents) {
      (eventsByDriver[selectedId] || []).forEach((e) => {
        if (e.latitude == null || e.longitude == null) return;
        out.push({ lat: Number(e.latitude), lng: Number(e.longitude), color: '#a371f7', size: 4, shape: 'diamond', title: `${e.event_type} ${fmtClock(tsMs(e.created_at))}${e.address ? ' · ' + e.address : ''}`, jump: tsMs(e.created_at), z: 52 });
      });
    }
    selLoads.forEach((l) => (l.load_stops || []).forEach((s) => {
      if (s.latitude == null || s.longitude == null) return;
      const done = stopArr(s) != null;
      out.push({ lat: Number(s.latitude), lng: Number(s.longitude), shape: 'square', size: 6, color: s.stop_type === 'pickup' ? '#58a6ff' : '#3fb950', stroke: done ? '#f0f6fc' : '#0d1117', label: s.stop_type === 'pickup' ? 'P' : 'D', title: `#${l.load_number} ${s.stop_type}: ${stopLabel(s)}${stopPlan(s) ? ' · plan ' + fmtClock(stopPlan(s)) : ''}${done ? ' · arrived ' + fmtClock(stopArr(s)) : ''}`, jump: stopArr(s) || stopPlan(s), z: 58 });
    }));
    return out;
  }, [selectedId, settings.showStops, settings.showEvents, selStops, selSpeeding, eventsByDriver, selLoads]);

  const heatRects = useMemo(() => {
    if (!settings.heat) return [];
    const trs = visible.map((f) => tracks[f.id]).filter(Boolean);
    const cells = coverageCells(trs, 0.01);
    const max = cells.reduce((m, c) => Math.max(m, c.n), 1);
    return cells.map((c) => {
      const f = Math.log(1 + c.n) / Math.log(1 + max);
      return { lat: c.lat, lng: c.lng, size: 0.01, color: f > 0.66 ? '#f85149' : f > 0.33 ? '#f0883e' : '#d29922', opacity: 0.12 + 0.45 * f };
    });
  }, [settings.heat, visible, tracks]);

  // ---- actions ----
  const fitFleet = useCallback(() => setFitReq((r) => ({ n: r.n + 1, mode: 'fleet' })), []);
  const fitDay = useCallback(() => setFitReq((r) => ({ n: r.n + 1, mode: 'day' })), []);
  const fitPoints = useMemo(() => {
    if (fitReq.mode === 'day' && selTrack.length) return selTrack.filter((_, i) => i % 10 === 0 || i === selTrack.length - 1).map((p) => ({ lat: p.lat, lng: p.lng }));
    return markers.map((m) => ({ lat: m.lat, lng: m.lng }));
  }, [fitReq.mode, selTrack, markers]);

  // fit once, after the first data arrives
  useEffect(() => {
    if (!autoFitDone.current && mapsState.status === 'ready' && markers.length) { autoFitDone.current = true; fitFleet(); }
  }, [markers.length, mapsState.status, fitFleet]);

  // follow mode: keep the selected truck centred
  useEffect(() => {
    if (follow && selected && selected.pos) setPanTarget({ lat: selected.pos.lat, lng: selected.pos.lng });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [follow, selected && selected.pos && selected.pos.lat, selected && selected.pos && selected.pos.lng]);

  const selectDriver = useCallback((id) => {
    setSelectedId((cur) => (cur === id ? null : id));
    setTab('history');
    fitDay();
  }, [fitDay]);

  const jumpTo = useCallback((ms, latlng) => {
    if (ms == null) return;
    setLive(false);
    setPlaying(false);
    setCursor(ms);
    if (latlng) setPanTarget({ ...latlng });
  }, []);

  const onMapClick = useCallback((ll) => {
    if (measureOn) setMeasurePts((p) => p.concat([ll]));
  }, [measureOn]);

  const onDotClick = useCallback((idx) => {
    const d = dotList[idx];
    if (d && d.jump != null) jumpTo(d.jump, null);
  }, [dotList, jumpTo]);

  const onMarkerHover = useCallback((id, e) => {
    const r = mapBoxRef.current ? mapBoxRef.current.getBoundingClientRect() : { left: 0, top: 0, width: 800, height: 500 };
    setHover({ id, x: e.clientX - r.left, y: e.clientY - r.top, w: r.width, h: r.height });
  }, []);
  const onMarkerOut = useCallback(() => setHover(null), []);

  const exportSelected = useCallback((kind) => {
    if (!selected || !selTrack.length) return;
    const base = `trail_${(selected.name || 'driver').replace(/\W+/g, '_')}_${ymd}`;
    if (kind === 'csv') download(base + '.csv', trackToCsv(selTrack, { driver: selected.name, truck: selected.truck ? selected.truck.truck_number : '' }), 'text/csv;charset=utf-8;');
    else download(base + '.gpx', trackToGpx(selTrack, { name: `${selected.name} ${ymd}`, gapMin: GAP_MIN, stops: selStops }), 'application/gpx+xml');
  }, [selected, selTrack, selStops, ymd]);

  const shiftDay = (delta) => {
    const b = dayBounds(ymd);
    if (!b) return;
    const next = localYmd(b.startMs + delta * 86400000 + 3 * 3600000);
    if (next > localYmd()) return;
    setPlaying(false);
    setYmd(next);
  };

  const goLive = () => {
    setPlaying(false);
    if (!isToday) setYmd(localYmd());
    setLive(true);
    setCursor(Date.now());
  };

  // keyboard: space play/pause, arrows step 1 min (shift = 10 min)
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || '';
      if (/INPUT|SELECT|TEXTAREA/.test(tag) || !wrapRef.current) return;
      if (!wrapRef.current.contains(document.activeElement) && document.activeElement !== document.body) return;
      if (e.code === 'Space') { e.preventDefault(); setLive(false); setPlaying((p) => !p); }
      if (e.code === 'ArrowRight' || e.code === 'ArrowLeft') {
        e.preventDefault();
        const step = (e.shiftKey ? 10 : 1) * 60000 * (e.code === 'ArrowRight' ? 1 : -1);
        setLive(false);
        setCursor((c) => (bounds ? Math.min(Math.max(c + step, bounds.startMs), Math.min(bounds.endMs - 1, Date.now())) : c));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [bounds]);

  // ---- render helpers ----
  const dayLen = bounds ? bounds.endMs - bounds.startMs : 86400000;
  const pct = (ms) => `${Math.max(0, Math.min(100, ((ms - (bounds ? bounds.startMs : 0)) / dayLen) * 100))}%`;
  const cursorClamped = bounds ? Math.min(Math.max(cursor, bounds.startMs), bounds.endMs - 1) : cursor;
  const hoverF = hover ? fleet.find((f) => f.id === hover.id) : null;

  const renderLoadsGlimpse = (f, compact) => {
    const ls = f.loads;
    if (!ls.length) return <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>No loads assigned for this day.</div>;
    const nxt = nextStopOf(ls);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
        {nxt && (
          <div style={{ fontSize: '0.78rem', background: 'var(--surface-2)', borderRadius: 4, padding: '0.25rem 0.4rem' }}>
            <span style={{ color: 'var(--text-secondary)' }}>{nxt.at ? 'At: ' : 'Next: '}</span>
            <strong>{stopLabel(nxt.s)}</strong>
            <span style={{ color: 'var(--text-secondary)' }}> · #{nxt.l.load_number}</span>
            {nxt.plan != null && !nxt.at && (
              <span style={{ color: nxt.plan < now ? '#f85149' : 'var(--text-secondary)' }}> · plan {fmtClock(nxt.plan)}{nxt.plan < now ? ` (${fmtDur((now - nxt.plan) / 60000)} over)` : ''}</span>
            )}
          </div>
        )}
        {ls.slice(0, compact ? 4 : 50).map((l) => (
          <div key={l.id} style={{ fontSize: '0.75rem', borderLeft: `3px solid ${l.finished ? '#3fb950' : '#58a6ff'}`, paddingLeft: '0.4rem' }}>
            <div><strong>#{l.load_number || '—'}</strong> <span style={{ color: 'var(--text-secondary)' }}>{(l.status || '').replace(/_/g, ' ')}</span>{l.commodity ? <span style={{ color: 'var(--text-muted)' }}> · {l.commodity}</span> : null}</div>
            {(l.load_stops || []).map((s) => {
              const a = stopArr(s);
              const p = stopPlan(s);
              return (
                <div key={s.id} style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <span style={{ color: s.stop_type === 'pickup' ? '#58a6ff' : '#3fb950', fontWeight: 700 }}>{s.stop_type === 'pickup' ? 'P' : s.stop_type === 'delivery' ? 'D' : '•'}</span>{' '}
                  {stopLabel(s)}{' '}
                  {a != null ? <span style={{ color: '#3fb950' }}>✓ {fmtClock(a)}</span> : (p != null ? <span style={{ color: 'var(--text-muted)' }}>plan {fmtClock(p)}</span> : null)}
                </div>
              );
            })}
          </div>
        ))}
        {compact && ls.length > 4 && <div style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>+{ls.length - 4} more</div>}
      </div>
    );
  };

  const stateChip = (f) => {
    const meta = STATE_META[f.state] || STATE_META.nodata;
    return chip(f.speeding ? '#f85149' : meta.color, f.speeding ? `${Math.round(f.pos.spd)} km/h!` : meta.label);
  };

  const ageText = (f) => {
    if (f.state === 'offshift') return f.lastT ? `last seen ${fmtClock(f.lastT)}` : 'no position before sign-off';
    if (!f.lastT) return 'no GPS';
    if (live && isToday) return fmtAge(now - f.lastT);
    const p = f.pos;
    if (p && p.after) return `last ping ${fmtClock(p.t)}`;
    if (p && p.gap) return `GPS gap since ${fmtClock(p.t)}`;
    return p && p.interpolated ? 'interpolated' : `ping ${fmtClock(f.lastT)}`;
  };

  return (
    <div ref={wrapRef} tabIndex={-1} style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', outline: 'none' }}>
      {/* Top bar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {chip(STATE_META.moving.color, `${counts.moving} moving`)}
          {chip(STATE_META.idle.color, `${counts.idle} stopped`)}
          {chip(STATE_META.stale.color, `${counts.stale} stale`)}
          {chip(STATE_META.offline.color, `${counts.offline + counts.nodata} offline`)}
          {counts.offshift > 0 && chip(STATE_META.offshift.color, `${counts.offshift} off shift`)}
          {speeders.length > 0 && chip('#f85149', `⚠ ${speeders.map((f) => `${f.truck ? f.truck.truck_number : f.name} ${Math.round(f.pos.spd)} km/h`).join(', ')}`, { animation: 'pulse 2s infinite' })}
        </div>
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', color: 'var(--text-muted)', fontSize: '0.75rem' }}>
          {loading && progress ? <span>Loading GPS {progress.done}/{progress.total}…</span> : null}
          {isToday && lastPoll ? <span title="New pings are fetched every 15 seconds">Updated {lastPoll.toLocaleTimeString()}</span> : null}
          <button style={btn(false)} onClick={() => loadDay()} title="Reload this day">↻</button>
        </div>
      </div>

      {rosterError && <div style={{ color: 'var(--color-danger-emphasis)', fontSize: '0.85rem' }}>Could not read drivers: {rosterError}</div>}

      {/* Sidebar + map */}
      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', height: 'calc(100vh - 420px)', minHeight: '520px' }}>
        <div style={{ flex: '0 0 330px', maxWidth: '100%', display: 'flex', flexDirection: 'column', background: 'var(--surface-2)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', minHeight: 0, height: '100%' }}>
          <div style={{ display: 'flex', gap: '0.3rem', padding: '0.5rem', borderBottom: '1px solid var(--border-default)' }}>
            <button style={btn(tab === 'fleet')} onClick={() => setTab('fleet')}>Fleet</button>
            <button style={btn(tab === 'history')} onClick={() => setTab('history')} disabled={!selectedId} title={selectedId ? '' : 'Pick a driver first'}>History</button>
            <button style={btn(tab === 'tools')} onClick={() => setTab('tools')}>Tools</button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem', minHeight: 0 }}>
            {tab === 'fleet' && (
              <>
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search driver, truck, plate, load #" style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: '0.4rem' }} />
                <div style={{ display: 'flex', gap: '0.3rem', marginBottom: '0.4rem', flexWrap: 'wrap' }}>
                  <button style={btn(false)} onClick={fitFleet}>Fit fleet</button>
                  <button style={btn(false)} onClick={() => setHidden({})}>Show all</button>
                  <button style={btn(false)} onClick={() => { const h = {}; listed.forEach((f) => { h[f.id] = true; }); setHidden(h); }}>Hide all</button>
                </div>
                {listed.length === 0 && !loading && <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: '0.5rem 0' }}>No driver has GPS or a load on this day.</div>}
                {listed.map((f) => (
                  <div key={f.id}
                    onClick={() => selectDriver(f.id)}
                    style={{ display: 'flex', gap: '0.45rem', alignItems: 'flex-start', padding: '0.45rem 0.4rem', borderRadius: 'var(--radius-md)', cursor: 'pointer', marginBottom: 2,
                      background: f.id === selectedId ? 'var(--surface-3)' : 'transparent', border: `1px solid ${f.id === selectedId ? 'var(--border-emphasis)' : 'transparent'}`, opacity: hidden[f.id] ? 0.45 : 1 }}>
                    <input type="checkbox" checked={!hidden[f.id]} onClick={(e) => e.stopPropagation()} onChange={() => setHidden((h) => ({ ...h, [f.id]: !h[f.id] }))} style={{ marginTop: 3 }} title="Show on map" />
                    <span style={{ width: 10, height: 10, borderRadius: 5, background: f.color, marginTop: 4, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 4 }}>
                        <span style={{ fontWeight: 600, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                        {stateChip(f)}
                      </div>
                      <div style={{ color: 'var(--text-secondary)', fontSize: '0.72rem', display: 'flex', justifyContent: 'space-between', gap: 4 }}>
                        <span>{f.truck ? `Truck ${f.truck.truck_number}` : 'No truck'}{f.loads.length ? ` · ${f.loads.filter((l) => !l.finished).length}/${f.loads.length} loads` : ''}</span>
                        <span>{f.state === 'moving' && f.pos && f.pos.spd != null ? `${Math.round(f.pos.spd)} km/h · ` : ''}{ageText(f)}</span>
                      </div>
                    </div>
                  </div>
                ))}
                {hiddenCount > 0 && <div style={{ color: 'var(--text-muted)', fontSize: '0.72rem', marginTop: '0.4rem' }}>{hiddenCount} other driver{hiddenCount === 1 ? '' : 's'} have no GPS or loads recently{search ? ' or do not match the search' : ''}.</div>}
              </>
            )}

            {tab === 'history' && selected && (
              <>
                <div style={{ display: 'flex', gap: '0.3rem', marginBottom: '0.4rem', flexWrap: 'wrap' }}>
                  <button style={btn(follow)} onClick={() => setFollow((v) => !v)} title="Keep this truck centred">{follow ? '◉ Following' : '○ Follow'}</button>
                  <button style={btn(false)} onClick={fitDay} disabled={!selTrack.length}>Fit day</button>
                </div>
                <FleetHistoryPanel
                  driverName={selected.name}
                  truckLabel={selected.truck ? `Truck ${selected.truck.truck_number}${selected.truck.license_plate ? ' · ' + selected.truck.license_plate : ''}` : 'No truck on file'}
                  track={selTrack}
                  stops={selStops}
                  gaps={selGaps}
                  summary={selSummary}
                  speeding={selSpeeding}
                  events={eventsByDriver[selectedId] || []}
                  shifts={shiftsByDriver[selectedId] || []}
                  loads={selLoads}
                  compare={selCompare}
                  speedLimit={settings.speedLimit}
                  onJump={jumpTo}
                  onExport={exportSelected}
                  onClose={() => { setSelectedId(null); setFollow(false); setTab('fleet'); }}
                />
              </>
            )}
            {tab === 'history' && !selected && <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Click a driver in the list or a truck on the map.</div>}

            {tab === 'tools' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem', fontSize: '0.82rem' }}>
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Distance measure</div>
                  <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button style={btn(measureOn)} onClick={() => setMeasureOn((v) => !v)}>{measureOn ? 'Measuring… (click the map)' : 'Measure'}</button>
                    <button style={btn(false)} onClick={() => setMeasurePts([])} disabled={!measurePts.length}>Clear</button>
                    {measurePts.length >= 2 && <strong>{pathKm(measurePts)} km</strong>}
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.72rem', marginTop: 3 }}>Straight-line legs between the points you click.</div>
                </div>
                <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>Speed alert above (km/h)
                  <input type="number" min={40} max={200} value={settings.speedLimit} onChange={(e) => setSetting('speedLimit', Math.max(1, Number(e.target.value) || 105))} style={{ ...inputStyle, width: 70 }} /></label>
                <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>Stale after (min without a ping)
                  <input type="number" min={2} max={29} value={settings.staleMin} onChange={(e) => setSetting('staleMin', Math.min(29, Math.max(2, Number(e.target.value) || 10)))} style={{ ...inputStyle, width: 70 }} /></label>
                <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>A stop is at least (min)
                  <input type="number" min={1} max={120} value={settings.dwellMin} onChange={(e) => setSetting('dwellMin', Math.max(1, Number(e.target.value) || 5))} style={{ ...inputStyle, width: 70 }} /></label>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={settings.heat} onChange={(e) => setSetting('heat', e.target.checked)} /> Coverage heat (where the visible trucks went this day)</label>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={settings.showStops} onChange={(e) => setSetting('showStops', e.target.checked)} /> Show stops + speeding on the selected trail</label>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={settings.showEvents} onChange={(e) => setSetting('showEvents', e.target.checked)} /> Show geofence events on the selected trail</label>
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Trail colours (selected driver)</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {SPEED_BANDS.map((b) => <span key={b.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.72rem' }}><span style={{ width: 14, height: 4, background: b.color, borderRadius: 2 }} />{b.label} km/h</span>)}
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.72rem' }}><span style={{ width: 14, borderTop: '2px dashed #f85149' }} />GPS gap</span>
                  </div>
                </div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>Keys: Space = play/pause, ← → = 1 min, Shift+← → = 10 min.</div>
              </div>
            )}
          </div>
        </div>

        <div ref={mapBoxRef} style={{ flex: '1 1 420px', position: 'relative', borderRadius: 'var(--radius-lg)', overflow: 'hidden', border: '1px solid var(--border-default)', background: '#242f3e', minHeight: 360, height: '100%' }}>
          {mapsState.status === 'ready' ? (
            <FleetTrackingMap
              ready
              markers={markers}
              polylines={polylines}
              dots={dotList}
              rects={heatRects}
              measurePath={measurePts}
              onMarkerHover={onMarkerHover}
              onMarkerOut={onMarkerOut}
              onMarkerClick={selectDriver}
              onMapClick={onMapClick}
              onDotClick={onDotClick}
              fitToken={fitReq.n}
              fitPoints={fitPoints}
              panTarget={panTarget}
              cursorStyle={measureOn ? 'crosshair' : null}
            />
          ) : (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', textAlign: 'center', color: mapsState.status === 'error' ? 'var(--color-warning-emphasis)' : 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: 1.5 }}>
              {mapsState.status === 'error' ? mapsState.message : 'Loading map…'}
            </div>
          )}

          {/* time badge */}
          <div style={{ position: 'absolute', left: 10, top: 10, background: 'rgba(13,17,23,.88)', border: '1px solid var(--border-default)', borderRadius: 8, padding: '0.3rem 0.6rem', fontSize: '0.85rem', pointerEvents: 'none' }}>
            {live && isToday ? <span style={{ color: '#3fb950', fontWeight: 700 }}>● LIVE</span> : <span style={{ color: '#d29922', fontWeight: 700 }}>⏮ REPLAY</span>}
            <span style={{ marginLeft: 8, fontVariantNumeric: 'tabular-nums' }}>{new Date(cursorClamped).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} {fmtClock(cursorClamped, true)}</span>
          </div>

          {/* hover glimpse */}
          {hoverF && (
            <div style={{
              position: 'absolute', zIndex: 20, pointerEvents: 'none', width: 300, maxHeight: 360, overflow: 'hidden',
              left: Math.min(hover.x + 16, (hover.w || 800) - 310), top: Math.max(8, Math.min(hover.y - 20, (hover.h || 500) - 300)),
              background: 'rgba(22,27,34,.97)', border: '1px solid var(--border-emphasis)', borderRadius: 10, padding: '0.6rem 0.7rem', boxShadow: 'var(--shadow-lg)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                <strong style={{ fontSize: '0.92rem' }}>{hoverF.name}</strong>{stateChip(hoverF)}
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginBottom: '0.35rem' }}>
                {hoverF.truck ? `Truck ${hoverF.truck.truck_number}${hoverF.truck.license_plate ? ' · ' + hoverF.truck.license_plate : ''}` : 'No truck on file'}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4, fontSize: '0.75rem', marginBottom: '0.4rem' }}>
                <div><div style={{ color: 'var(--text-muted)', fontSize: '0.62rem' }}>SPEED</div>{hoverF.pos && hoverF.pos.spd != null ? `${Math.round(hoverF.pos.spd)} km/h` : '—'}</div>
                <div><div style={{ color: 'var(--text-muted)', fontSize: '0.62rem' }}>{live && isToday ? 'LAST PING' : 'POSITION'}</div>{ageText(hoverF)}</div>
                <div><div style={{ color: 'var(--text-muted)', fontSize: '0.62rem' }}>{hoverF.dwellStop ? 'STOPPED FOR' : 'HEADING'}</div>{hoverF.dwellStop ? fmtDur((cursorClamped - hoverF.dwellStop.arriveMs) / 60000) : (hoverF.pos && hoverF.pos.hdg != null && hoverF.state === 'moving' ? `${Math.round(hoverF.pos.hdg)}°` : '—')}</div>
              </div>
              {renderLoadsGlimpse(hoverF, true)}
              <div style={{ color: 'var(--text-muted)', fontSize: '0.65rem', marginTop: 6 }}>Click for the full day history</div>
            </div>
          )}
        </div>
      </div>

      {/* Time bar */}
      <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', padding: '0.6rem 0.75rem' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center', marginBottom: '0.4rem' }}>
          <button style={btn(false)} onClick={() => shiftDay(-1)} title="Previous day">◀</button>
          <input type="date" value={ymd} max={localYmd()} onChange={(e) => { if (e.target.value) { setPlaying(false); setYmd(e.target.value); } }} style={inputStyle} />
          <button style={btn(false)} onClick={() => shiftDay(1)} disabled={isToday} title="Next day">▶</button>
          <button style={btn(live && isToday)} onClick={goLive}>● Live</button>
          <span style={{ width: 1, height: 22, background: 'var(--border-default)', margin: '0 0.2rem' }} />
          <button style={btn(playing)} onClick={() => { setLive(false); if (!playing && bounds && cursorClamped >= Math.min(bounds.endMs - 1, Date.now()) - 1000) setCursor(bounds.startMs); setPlaying((p) => !p); }}>{playing ? '❚❚ Pause' : '► Play'}</button>
          {[1, 10, 60, 300].map((s) => <button key={s} style={btn(playSpeed === s)} onClick={() => setPlaySpeed(s)}>{s}×</button>)}
          <span style={{ width: 1, height: 22, background: 'var(--border-default)', margin: '0 0.2rem' }} />
          <label style={{ color: 'var(--text-secondary)', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 4 }}>Trails
            <select value={settings.trailMin} onChange={(e) => setSetting('trailMin', Number(e.target.value))} style={inputStyle}>
              <option value={0}>off</option><option value={15}>15 min</option><option value={30}>30 min</option><option value={60}>1 h</option><option value={120}>2 h</option><option value={240}>4 h</option><option value={1440}>all day</option>
            </select>
          </label>
          <span style={{ marginLeft: 'auto', fontWeight: 700, fontSize: '1.05rem', fontVariantNumeric: 'tabular-nums' }}>{fmtClock(cursorClamped, true)}</span>
        </div>

        {/* slider with hour ticks */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{ width: 130, flexShrink: 0, fontSize: '0.7rem', color: 'var(--text-muted)' }}>
            <button style={{ ...btn(false), padding: '0.15rem 0.4rem', fontSize: '0.7rem' }} onClick={() => setSetting('laneOpen', !settings.laneOpen)}>{settings.laneOpen ? '▾' : '▸'} Lanes</button>
          </div>
          <div style={{ flex: 1, position: 'relative' }}>
            <input type="range" min={0} max={Math.round(dayLen / 1000) - 1} step={15}
              value={Math.round((cursorClamped - (bounds ? bounds.startMs : 0)) / 1000)}
              onChange={(e) => { if (!bounds) return; const v = bounds.startMs + Number(e.target.value) * 1000; setLive(false); setCursor(Math.min(v, Date.now())); }}
              style={{ width: '100%', accentColor: '#58a6ff' }} />
            <div style={{ position: 'relative', height: 12, fontSize: '0.6rem', color: 'var(--text-muted)' }}>
              {Array.from({ length: 9 }, (_, i) => i * 3).map((h) => (
                <span key={h} style={{ position: 'absolute', left: `${(h / 24) * 100}%`, transform: 'translateX(-50%)' }}>{h === 0 ? '12a' : h === 12 ? '12p' : h > 12 ? `${h - 12}p` : `${h}a`}</span>
              ))}
              {isToday && <span style={{ position: 'absolute', left: pct(now), top: -18, width: 2, height: 10, background: '#3fb950' }} title="now" />}
            </div>
          </div>
        </div>

        {/* per-driver activity lanes */}
        {settings.laneOpen && (
          <div style={{ maxHeight: 210, overflowY: 'auto', marginTop: '0.3rem' }}>
            {visible.filter((f) => tracks[f.id] && tracks[f.id].length).map((f) => {
              const a = analysis[f.id];
              const sh = shiftsByDriver[f.id] || [];
              const ev = (eventsByDriver[f.id] || []).filter((e) => ['enter', 'exit', 'dwell', 'completion_pending', 'drive_by', 'late_arrival'].includes(e.event_type));
              const visits = (loadsByDriver[f.id] || []).flatMap((l) => (l.load_stops || []).map((s) => ({ s, l, a: stopArr(s), p: stopPlan(s) })));
              return (
                <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', height: 22 }}>
                  <div onClick={() => selectDriver(f.id)} style={{ width: 130, flexShrink: 0, fontSize: '0.72rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer', fontWeight: f.id === selectedId ? 700 : 400 }}>
                    <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 4, background: f.color, marginRight: 5 }} />{f.truck ? `${f.truck.truck_number} · ` : ''}{f.name}
                  </div>
                  <div
                    onClick={(e) => { if (!bounds) return; const r = e.currentTarget.getBoundingClientRect(); const ms = bounds.startMs + ((e.clientX - r.left) / r.width) * dayLen; setSelectedId(f.id); jumpTo(Math.min(ms, Date.now()), null); }}
                    style={{ flex: 1, position: 'relative', height: 14, background: 'var(--bg-primary)', borderRadius: 3, cursor: 'pointer', outline: f.id === selectedId ? '1px solid var(--border-emphasis)' : 'none' }}
                    title="Click to jump here">
                    {sh.map((s, i) => {
                      const st = tsMs(s.signed_in_at), en = tsMs(s.signed_out_at) || now;
                      return <div key={'s' + i} style={{ position: 'absolute', left: pct(st), width: `calc(${pct(en)} - ${pct(st)})`, top: -2, height: 2, background: '#8b949e' }} title={`On shift ${fmtClock(st)} – ${s.signed_out_at ? fmtClock(en) : 'now'}`} />;
                    })}
                    {(a ? a.blocks : []).map((b, i) => (
                      <div key={i} style={{
                        position: 'absolute', left: pct(b.startMs), width: `max(1px, calc(${pct(b.endMs)} - ${pct(b.startMs)}))`, top: 2, bottom: 2, borderRadius: 2,
                        background: b.kind === 'move' ? f.color : b.kind === 'stop' ? '#d2992299' : 'repeating-linear-gradient(45deg, #f8514966 0 3px, transparent 3px 6px)',
                      }} title={`${b.kind === 'move' ? 'Driving' : b.kind === 'stop' ? 'Stopped' : 'No GPS'} ${fmtClock(b.startMs)} – ${fmtClock(b.endMs)} (${fmtDur((b.endMs - b.startMs) / 60000)})`} />
                    ))}
                    {visits.filter((v) => v.p != null).map((v, i) => (
                      <div key={'p' + i} style={{ position: 'absolute', left: pct(v.p), bottom: -3, width: 0, height: 0, borderLeft: '3px solid transparent', borderRight: '3px solid transparent', borderBottom: '5px solid #f0f6fc', transform: 'translateX(-3px)' }} title={`Planned ${v.s.stop_type} #${v.l.load_number} ${fmtClock(v.p)}`} />
                    ))}
                    {visits.filter((v) => v.a != null).map((v, i) => (
                      <div key={'a' + i} style={{ position: 'absolute', left: pct(v.a), top: 0, bottom: 0, width: 2, background: v.s.stop_type === 'pickup' ? '#58a6ff' : '#3fb950' }} title={`Arrived ${v.s.stop_type} #${v.l.load_number} ${fmtClock(v.a)}`} />
                    ))}
                    {ev.map((e, i) => (
                      <div key={'e' + i} style={{ position: 'absolute', left: pct(tsMs(e.created_at)), top: -1, width: 4, height: 4, marginLeft: -2, borderRadius: 2, background: '#a371f7' }} title={`${e.event_type} ${fmtClock(tsMs(e.created_at))}`} />
                    ))}
                    <div style={{ position: 'absolute', left: pct(cursorClamped), top: -3, bottom: -3, width: 2, background: '#f0f6fc', pointerEvents: 'none' }} />
                  </div>
                </div>
              );
            })}
            {visible.every((f) => !(tracks[f.id] && tracks[f.id].length)) && !loading && (
              <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', padding: '0.3rem 0' }}>No GPS recorded on this day for the drivers shown.</div>
            )}
            <div style={{ display: 'flex', gap: '0.8rem', fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: 4, flexWrap: 'wrap' }}>
              <span><span style={{ display: 'inline-block', width: 12, height: 6, background: '#58a6ff', marginRight: 3 }} />driving (driver colour)</span>
              <span><span style={{ display: 'inline-block', width: 12, height: 6, background: '#d2992299', marginRight: 3 }} />stopped ≥ {settings.dwellMin} min</span>
              <span><span style={{ display: 'inline-block', width: 12, height: 6, background: 'repeating-linear-gradient(45deg, #f8514966 0 3px, transparent 3px 6px)', marginRight: 3 }} />no GPS &gt; {GAP_MIN} min</span>
              <span><span style={{ display: 'inline-block', width: 12, height: 2, background: '#8b949e', marginRight: 3 }} />on shift</span>
              <span>▲ planned stop · | arrived (blue pickup, green delivery) · <span style={{ color: '#a371f7' }}>●</span> geofence event</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default FleetTrackingView;
